// "Unpack this question" on the mock report: starts a card, polls for it, and
// reveals it — flipping `hidden` on blocks the server already rendered.
//
// NOT IN packs/, for the reason mock_composer.js records; imported by
// packs/application.js beside course_report_poll.js, whose shape this is.
//
// Four states live in mock_reports/_elaboration.html.erb and the server
// decides the first one; this module only ever moves between them. The card's
// HTML comes back in the poll payload, rendered by the same helpers as the
// rationale above it, so the browser inserts it and typesets its maths — it
// never assembles a card from fields.
//
// Gives up when the row says it may be restarted (failed, or stranded past
// the server's own STALE_AFTER — the payload reports that the moment it is
// true, so a reader who joins somebody else's stalled wait is not held to a
// clock of their own), or when this browser's patience runs out: STALE_AFTER
// is shipped in the data attribute from the model, so the browser's and the
// watchdog's are one number. That second clock is checked in `schedule`, on
// EVERY path that loops — a stalled row, a 404 after the key moved under an
// expert's edit, a lapsed session, a run of 5xx — so no failure mode polls
// for ever with "Try again" never revealed. A 402 reveals the top-up link the
// server already rendered beside the button, never in place of it — a
// balance topped up in another tab must not lock the button until a reload.

import typesetMath from "./math_typeset";

const FIRST_POLL_MS = 2000;
const MAX_POLL_MS = 6000;
const GRACE_MS = 30000;

function csrfToken() {
  const meta = document.querySelector("meta[name=csrf-token]");
  return meta ? meta.content : "";
}

function setupCard(root) {
  const url = root.dataset.url;
  const staleAfterMs = Number(root.dataset.staleAfterMs) || 360000;

  const blocks = {
    offer: root.querySelector("[data-elaboration-offer]"),
    working: root.querySelector("[data-elaboration-working]"),
    failed: root.querySelector("[data-elaboration-failed]"),
    ready: root.querySelector("[data-elaboration-ready]"),
  };
  const body = root.querySelector("[data-elaboration-body]");
  const error = root.querySelector("[data-elaboration-error]");
  const failedMessage = root.querySelector("[data-elaboration-failed-message]");
  const topup = root.querySelector("[data-elaboration-topup]");
  const toggle = root.querySelector("[data-elaboration-toggle]");
  const free = root.querySelector("[data-elaboration-free]");

  let startedAt = 0;
  let timer = null;

  function show(state) {
    Object.entries(blocks).forEach(([name, el]) => {
      if (el) el.hidden = name !== state;
    });
    root.dataset.state = state;
  }

  function fail(message) {
    if (failedMessage && message) failedMessage.textContent = message;
    show("failed");
  }

  // `html` is the server's own render of mock_reports/_elaboration_body — the
  // model's text escaped through mock_prose/mock_inline, the same helpers the
  // rationale beside it uses, from a same-origin JSON reply. It is exactly
  // what the page would have carried had the card existed at page load, so
  // inserting it is the same trust as the page itself.
  function land(html) {
    if (body) {
      body.innerHTML = html;
      typesetMath(body);
      body.hidden = false;
    }
    if (toggle) toggle.setAttribute("aria-expanded", "true");
    // "Already written — free to read" is true of the NEXT reader. The one
    // who just watched it land has paid for it, so the line comes off.
    if (free) free.hidden = true;
    show("ready");
  }

  function apply(data) {
    if (data.readable && data.html) return land(data.html);
    // Before `working`: a stranded row reports both, and its `error` is the
    // retry the server is already offering.
    if (data.restartable) return fail(data.error);
    if (data.working) return schedule();
    return fail(data.error);
  }

  function poll() {
    fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => (data === null ? schedule() : apply(data)))
      .catch(schedule);
  }

  // The one give-up clock. Every path that keeps polling comes through here,
  // so a reply the page cannot read is bounded exactly as a slow row is.
  function schedule() {
    const elapsed = Date.now() - startedAt;
    if (elapsed > staleAfterMs + GRACE_MS) {
      return fail("That one is taking longer than it should. Try again — you have not been charged for it.");
    }
    const wait = Math.min(MAX_POLL_MS, FIRST_POLL_MS + elapsed / 10);
    timer = window.setTimeout(poll, wait);
  }

  function start() {
    if (error) error.hidden = true;
    window.clearTimeout(timer);
    startedAt = Date.now();
    show("working");

    fetch(url, {
      method: "POST",
      headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
      credentials: "same-origin",
    })
      .then((response) => response.json().then((data) => ({ status: response.status, data })))
      .then(({ status, data }) => {
        if (status === 402) {
          if (topup) topup.hidden = false;
          return refuse(data.message || "You do not have enough tokens for this one yet.");
        }
        if (status === 429) return refuse(data.message || "You have asked for a lot of these in the last hour. Try again later.");
        if (status >= 400) return refuse("We could not start this one. Try again in a moment.");
        return apply(data);
      })
      .catch(() => refuse("We could not reach the server. Check your connection and try again."));
  }

  // Back to the offer, with the reason under the button. Nothing was spent.
  function refuse(message) {
    if (error) {
      error.textContent = message;
      error.hidden = false;
    }
    show("offer");
  }

  root.querySelectorAll("[data-elaboration-start]").forEach((button) => {
    button.addEventListener("click", start);
  });

  if (toggle && body) {
    toggle.addEventListener("click", () => {
      const open = body.hidden;
      body.hidden = !open;
      toggle.setAttribute("aria-expanded", open ? "true" : "false");
    });
  }

  // Arrived while somebody else's generation was in flight: join their wait.
  if (root.dataset.state === "working") {
    startedAt = Date.now();
    schedule();
  }
}

function setup() {
  document.querySelectorAll("[data-elaboration]").forEach(setupCard);
}

// The same guard every module in this pack carries — see mock_request_wizard.js.
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", setup);
} else {
  setup();
}
